import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Instagram, Save, Copy, ExternalLink, RefreshCw } from 'lucide-react';

const InstagramPostManager = () => {
  const [postUrls, setPostUrls] = useState([
    'https://www.instagram.com/p/DPHRPlmkyU6/?igsh=MXRqNzc4aThjMzJlOQ==',
    'https://www.instagram.com/p/DPHXb1Aj8s3/?igsh=eGtlbHpudHQ1bXZk',
    'https://www.instagram.com/p/DPHZe4WjaV6/?igsh=aDBjdW5nNmJ0ZWNk',
    'https://www.instagram.com/reel/Cp-TR6Jg5Mi/?igsh=MWwzYjg3N3FvMDNsNA=='
  ]);
  
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleUrlChange = (index, value) => {
    const newUrls = [...postUrls];
    newUrls[index] = value;
    setPostUrls(newUrls);
  };

  const handleSave = async () => {
    setSaving(true);
    
    // In a real implementation, this would save to your backend/database
    // For now, we'll just simulate saving to localStorage
    try {
      localStorage.setItem('instagram_post_urls', JSON.stringify(postUrls));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving Instagram URLs:', error);
    }
    
    setSaving(false);
  };

  const validateInstagramUrl = (url) => {
    // Updated regex to handle URLs with query parameters and reel URLs
    const instagramRegex = /^https:\/\/www\.instagram\.com\/(p|reel)\/[A-Za-z0-9_-]+(\?[^\\s]*)?$/;
    return instagramRegex.test(url);
  };

  const refreshEmbeds = () => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  };

  const getInstructionSteps = () => [
    "Go to your Instagram profile: instagram.com/shoppersdiscountclub",
    "Find the post you want to display on your website",
    "Click on the post to open it",
    "Click the '...' menu (three dots) on the post",
    "Select 'Copy Link'",
    "Paste the link in the input field below",
    "Repeat for all 4 posts (choose your best recent posts)",
    "Click 'Save Changes' to update your website"
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <Instagram className="h-6 w-6 text-primary" />
            <h3 className="text-xl font-semibold text-charcoal">
              Instagram Post Manager
            </h3>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-900 mb-2">
              📋 How to Update Instagram Posts:
            </h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
              {getInstructionSteps().map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>

          <div className="space-y-4">
            {postUrls.map((url, index) => (
              <div key={index} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Instagram Post {index + 1}
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                    placeholder="https://www.instagram.com/p/POST_ID/"
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      validateInstagramUrl(url) 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-red-300 bg-red-50'
                    }`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(url, '_blank')}
                    disabled={!validateInstagramUrl(url)}
                    className="px-3"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                {!validateInstagramUrl(url) && url.length > 0 && (
                  <p className="text-red-600 text-xs">
                    Please enter a valid Instagram post URL
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || postUrls.some(url => !validateInstagramUrl(url))}
              className="bg-primary hover:bg-primary/90 text-white"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : saved ? (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Saved!
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
            
            <Button
              variant="outline"
              onClick={refreshEmbeds}
              className="border-primary text-primary hover:bg-primary hover:text-white"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Embeds
            </Button>
          </div>

          {saved && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-green-800 text-sm">
                ✅ Instagram post URLs have been updated successfully! 
                The changes will appear on the homepage.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <h4 className="font-medium text-charcoal mb-4">
            💡 Tips for Best Results:
          </h4>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Use your most recent and engaging posts for better visibility</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Mix different types of posts (deals, events, jobs, tips) for variety</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Update the posts weekly to keep content fresh</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary">•</span>
              <span>Embeds automatically refresh every 5 minutes on the homepage</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default InstagramPostManager;